import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Popover,
  Button,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
  Divider
} from '@mui/material';
import {
  FormatBold,
  Title,
  FormatClear
} from '@mui/icons-material';
import { 
  createFormattedText, 
  applyFormatting, 
  FORMAT_TYPES,
  mergeFormatting,
  removeFormatting,
  renderFormattedText
} from '../utils/textFormatting';

const FormattedTextEditor = forwardRef(({ 
  value, 
  onChange, 
  placeholder = "문장 내용을 입력하세요...",
  minRows = 15,
  maxRows = 40,
  disabled = false,
  onSelectionChange,
  disableFormatMenu = false
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const editorRef = useRef(null);
  const overlayRef = useRef(null);
  const [selectedText, setSelectedText] = useState('');
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  const [formatMenuAnchor, setFormatMenuAnchor] = useState(null);
  const [selectedTextPreview, setSelectedTextPreview] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0); // 모바일 키보드 높이 추적
  // TextArea 기반으로 단순화 - 불필요한 커서 위치 추적 상태 제거

  // 포맷팅된 텍스트 상태 관리
  const [formattedText, setFormattedText] = useState(() => {
    if (typeof value === 'string') {
      return createFormattedText(value);
    }
    return value || createFormattedText('');
  });

  // 텍스트 변경 시 formattedText 업데이트
  useEffect(() => {
    if (typeof value === 'string') {
      setFormattedText(createFormattedText(value));
    } else if (value) {
      setFormattedText(value);
    }
  }, [value]);

  // 모바일 키보드 높이 감지 (Visual Viewport API 사용)
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') {
      return;
    }

    // Visual Viewport API 지원 여부 확인
    if (window.visualViewport) {
      const handleViewportResize = () => {
        if (window.visualViewport) {
          // 전체 뷰포트 높이와 visual viewport 높이의 차이 = 키보드 높이
          const viewportHeight = window.visualViewport.height;
          const windowHeight = window.innerHeight;
          const keyboardHeight = Math.max(0, windowHeight - viewportHeight);
          setKeyboardHeight(keyboardHeight);
        }
      };

      // 초기 높이 설정
      handleViewportResize();

      // Visual Viewport 이벤트 리스너 등록
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);

      return () => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleViewportResize);
          window.visualViewport.removeEventListener('scroll', handleViewportResize);
        }
      };
    } else {
      // Visual Viewport API를 지원하지 않는 경우 폴백
      // window resize 이벤트로 대략적인 키보드 감지 시도
      let initialHeight = window.innerHeight;
      
      const handleResize = () => {
        const currentHeight = window.innerHeight;
        const heightDiff = initialHeight - currentHeight;
        // 키보드가 나타났다고 판단되는 임계값 (전체 화면의 20% 이상 줄어들면)
        if (heightDiff > initialHeight * 0.2) {
          setKeyboardHeight(heightDiff);
        } else {
          setKeyboardHeight(0);
        }
      };

      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isMobile]);

  // TextArea 기반으로 단순화 - 복잡한 커서 위치 관리 함수들 제거
  // TextArea는 자체적으로 커서 위치를 관리하므로 별도 함수 불필요

  // TextArea 기반 텍스트 선택 이벤트 핸들러 (단순화)
  const handleTextSelection = useCallback((e) => {
    if (!editorRef.current || isComposing) return;

    const textarea = e.target;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    if (start !== end) {
      const selectedText = textarea.value.substring(start, end).trim();
      if (selectedText) {
        setSelectedText(selectedText);
        // 선택된 텍스트 미리보기는 표시하지 않음 (폰트 설정만 표시)
        setSelectedTextPreview('');
        
        // 선택 상태 변경 알림
        if (onSelectionChange) {
          onSelectionChange(true);
        }
        
        // disableFormatMenu가 false일 때만 포맷팅 메뉴 표시
        if (!disableFormatMenu) {
          // 포맷팅 메뉴 표시 (항상 하단에 고정)
          setFormatMenuAnchor({
            top: window.innerHeight - 140,
            left: window.innerWidth / 2,
            anchorEl: editorRef.current
          });
        }
        
        // 포커스 유지 및 커서 깜빡임 보장
        requestAnimationFrame(() => {
          if (textarea) {
            textarea.focus();
            // 선택 영역 유지하여 사용자가 계속 편집할 수 있도록
            textarea.setSelectionRange(start, end);
          }
        });
      }
    } else {
      if (!disableFormatMenu) {
        setFormatMenuAnchor(null);
      }
      setSelectedTextPreview('');
      
      // 선택 상태 변경 알림
      if (onSelectionChange) {
        onSelectionChange(false);
      }
    }
  }, [isMobile, isComposing, disableFormatMenu, onSelectionChange]);

  // TextArea 기반으로 단순화된 텍스트 입력 핸들러 (더 이상 사용하지 않음)
  const handleInput = (e) => {
    // TextArea의 onChange로 대체됨
  };

  // TextArea 기반 포맷팅 적용 함수 (개선)
  const applyFormatToSelection = (formatType, formatValue = null) => {
    // IME 조합 중에는 포맷팅 적용하지 않음
    if (isComposing) return;
    
    const textarea = editorRef.current;
    if (!textarea) return;
    
    try {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      if (start === end) return; // 선택된 텍스트가 없으면 리턴
      
      if (start < 0 || end < 0 || start > formattedText.text.length || end > formattedText.text.length) {
        console.warn('Invalid selection range');
        return;
      }

      const selectedText = textarea.value.substring(start, end);
      if (!selectedText || selectedText.trim().length === 0) return; // 빈 텍스트는 포맷팅하지 않음

    // 포맷팅 적용
    const newFormatting = applyFormatting(
      formattedText.text,
      start,
      end,
      formatType,
      formatValue
    );

    const updatedFormatting = mergeFormatting(formattedText.formatting, newFormatting);
    
    const updatedFormattedText = {
      ...formattedText,
      formatting: updatedFormatting
    };

    setFormattedText(updatedFormattedText);
    onChange(updatedFormattedText);
    
    // 포맷팅 적용 후 TextArea 포커스 유지 및 선택 영역 제거
    // 여러 단계로 포커스와 커서 위치를 확실하게 유지
    requestAnimationFrame(() => {
      if (textarea) {
        textarea.focus();
        // 커서를 선택 영역 끝으로 이동하고 선택 해제
        textarea.setSelectionRange(end, end);
        
        // 추가로 한 번 더 확인하여 커서가 확실히 깜빡이도록 함
        setTimeout(() => {
          if (textarea && document.activeElement === textarea) {
            const currentPos = textarea.selectionStart;
            textarea.setSelectionRange(currentPos, currentPos);
          } else if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(end, end);
          }
        }, 50);
      }
    });
    
      // 포맷팅 적용 후 바텀시트는 유지 (사용자가 다른 포맷팅도 적용할 수 있도록)
      // 단, 선택 영역이 없어지면 바텀시트도 닫힘
      // 선택 영역을 제거하므로 바텀시트도 닫아야 함
      setTimeout(() => {
        setFormatMenuAnchor(null);
        setSelectedTextPreview('');
      }, 100);
    } catch (error) {
      console.error('포맷팅 적용 오류:', error);
      // 에러 발생 시에도 포커스 유지
      requestAnimationFrame(() => {
        if (textarea) {
          textarea.focus();
        }
      });
    }
  };

  // TextArea 기반으로 단순화 - DOM 관련 복잡한 함수들 제거
  // TextArea는 selectionStart/selectionEnd로 간단하게 처리 가능

  // 볼드 적용
  const handleBold = () => {
    applyFormatToSelection(FORMAT_TYPES.BOLD);
  };

  // 볼드 제거
  const handleRemoveBold = () => {
    const textarea = editorRef.current;
    if (!textarea) return;
    
    try {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      if (start === end) return; // 선택된 텍스트가 없으면 리턴
      
      if (start < 0 || end < 0 || start > formattedText.text.length || end > formattedText.text.length) {
        console.warn('Invalid selection range');
        return;
      }

      // 볼드 포맷팅만 제거
      const updatedFormatting = removeFormatting(
        formattedText.formatting,
        start,
        end,
        FORMAT_TYPES.BOLD
      );

      const updatedFormattedText = {
        ...formattedText,
        formatting: updatedFormatting
      };

      setFormattedText(updatedFormattedText);
      onChange(updatedFormattedText);
      
      // 포맷팅 제거 후 TextArea 포커스 유지 및 선택 영역 제거
      requestAnimationFrame(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(end, end);
          
          setTimeout(() => {
            if (textarea && document.activeElement === textarea) {
              const currentPos = textarea.selectionStart;
              textarea.setSelectionRange(currentPos, currentPos);
            } else if (textarea) {
              textarea.focus();
              textarea.setSelectionRange(end, end);
            }
          }, 50);
        }
      });
    } catch (error) {
      console.error('볼드 제거 오류:', error);
      requestAnimationFrame(() => {
        if (textarea) {
          textarea.focus();
        }
      });
    }
  };

  // 소제목 적용
  const handleTitle = () => {
    applyFormatToSelection(FORMAT_TYPES.TITLE);
  };

  // 외부에서 호출 가능한 메서드 노출
  useImperativeHandle(ref, () => ({
    applyBold: handleBold,
    removeBold: handleRemoveBold,
    applyTitle: handleTitle,
    clearFormatting: handleClearFormatting,
    hasSelection: () => {
      const textarea = editorRef.current;
      if (!textarea) return false;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      return start !== end && start >= 0 && end >= 0;
    }
  }));

  // TextArea 기반 포맷팅 제거 (개선)
  const handleClearFormatting = () => {
    const textarea = editorRef.current;
    if (!textarea) return;
    
    try {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      if (start === end) return; // 선택된 텍스트가 없으면 리턴
      
      if (start < 0 || end < 0 || start > formattedText.text.length || end > formattedText.text.length) {
        console.warn('Invalid selection range');
        return;
      }

      // 모든 포맷팅 타입에 대해 제거
      let updatedFormatting = formattedText.formatting;
    [FORMAT_TYPES.BOLD, FORMAT_TYPES.SIZE, FORMAT_TYPES.TITLE].forEach(formatType => {
      updatedFormatting = removeFormatting(
        updatedFormatting,
        start,
        end,
        formatType
      );
    });

    const updatedFormattedText = {
      ...formattedText,
      formatting: updatedFormatting
    };

    setFormattedText(updatedFormattedText);
    onChange(updatedFormattedText);
    
    // 포맷팅 제거 후 TextArea 포커스 유지 및 선택 영역 제거
    // 여러 단계로 포커스와 커서 위치를 확실하게 유지
    requestAnimationFrame(() => {
      if (textarea) {
        textarea.focus();
        // 커서를 선택 영역 끝으로 이동하고 선택 해제
        textarea.setSelectionRange(end, end);
        
        // 추가로 한 번 더 확인하여 커서가 확실히 깜빡이도록 함
        setTimeout(() => {
          if (textarea && document.activeElement === textarea) {
            const currentPos = textarea.selectionStart;
            textarea.setSelectionRange(currentPos, currentPos);
          } else if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(end, end);
          }
        }, 50);
      }
    });
    
      // 포맷팅 제거 후 바텀시트 닫기
      setTimeout(() => {
        setFormatMenuAnchor(null);
        setSelectedTextPreview('');
      }, 100);
    } catch (error) {
      console.error('포맷팅 제거 오류:', error);
      // 에러 발생 시에도 포커스 유지
      requestAnimationFrame(() => {
        if (textarea) {
          textarea.focus();
        }
      });
    }
  };

  // 포맷팅 메뉴 닫기
  const handleCloseFormatMenu = () => {
    setFormatMenuAnchor(null);
    setSelectedTextPreview('');
    
    // 포맷팅 메뉴 닫힐 때도 포커스 유지 및 커서 깜빡임 보장
    requestAnimationFrame(() => {
      const textarea = editorRef.current;
      if (textarea) {
        textarea.focus();
        const currentPos = textarea.selectionStart;
        // 커서 위치를 다시 설정하여 깜빡임 보장
        textarea.setSelectionRange(currentPos, currentPos);
        
        // 추가 확인
        setTimeout(() => {
          if (textarea && document.activeElement === textarea) {
            const pos = textarea.selectionStart;
            textarea.setSelectionRange(pos, pos);
          } else if (textarea) {
            textarea.focus();
            const pos = textarea.selectionStart || 0;
            textarea.setSelectionRange(pos, pos);
          }
        }, 30);
      }
    });
  };

  // TextArea 기반 키보드 이벤트 핸들러
  const handleKeyDown = (e) => {
    // ESC 키로 포맷팅 메뉴 닫기
    if (e.key === 'Escape' && formatMenuAnchor) {
      e.preventDefault();
      handleCloseFormatMenu();
    }
  };

  // 포커스 핸들러
  const handleFocus = () => {
    // 포커스 시에는 커서 위치를 조작하지 않음
    // 사용자가 원하는 위치에 커서를 유지할 수 있도록 함
  };

  // TextArea 기반으로 단순화 - 복잡한 useEffect 제거
  // TextArea는 value prop으로 자동 동기화되므로 별도 렌더링 로직 불필요

  // TextArea 기반 컴포지션 이벤트 핸들러 (단순화)
  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
    // TextArea는 커서 위치를 자동으로 관리하므로 별도 처리 불필요
  };

  // TextArea 스크롤과 포맷팅 미리보기 오버레이 동기화
  const handleScroll = useCallback(() => {
    const textarea = editorRef.current;
    const overlay = overlayRef.current;
    if (textarea && overlay) {
      overlay.scrollTop = textarea.scrollTop;
    }
  }, []);

  // 포맷팅 미리보기 오버레이와 TextArea의 스크롤 위치 동기화
  useEffect(() => {
    const textarea = editorRef.current;
    const overlay = overlayRef.current;
    if (!textarea || !overlay) return;

    // 스크롤 위치 동기화 함수
    const syncScroll = () => {
      if (textarea && overlay) {
        overlay.scrollTop = textarea.scrollTop;
      }
    };

    // 초기 스크롤 위치 동기화 (렌더링 후)
    const rafId = requestAnimationFrame(() => {
      syncScroll();
      // 추가로 한 번 더 확인하여 스크롤 위치 확실히 동기화
      requestAnimationFrame(syncScroll);
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [formattedText]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 하이브리드 디스플레이: TextArea + 포맷팅 미리보기 */}
      <Box sx={{ position: 'relative', flex: 1, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* 포맷팅 미리보기 오버레이 - 포맷팅 있을 때만 표시하여 텍스트 겹침 방지 */}
        <Box
          ref={overlayRef}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            visibility: formattedText.formatting && formattedText.formatting.length > 0 ? 'visible' : 'hidden',
            padding: { xs: '20px 16px', sm: '24px 20px' },
            fontSize: { xs: '16px', sm: '1.01rem' },
            lineHeight: '1.7',
            fontFamily: 'NanumSquareR, sans-serif',
            fontWeight: 400,
            letterSpacing: '-0.01em',
            color: '#111', // 항상 텍스트 표시 (선택 영역에서도 보이도록)
            background: 'transparent', // 배경은 항상 투명 (부모 배경 사용)
            boxSizing: 'border-box',
            pointerEvents: 'none',
            overflowY: 'auto', // 스크롤 가능하게 하여 TextArea와 동기화
            overflowX: 'hidden',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            zIndex: 10, // TextArea 위에 오도록 zIndex 증가 (선택 영역에서도 텍스트가 보이도록)
            // 포맷팅된 요소들의 스타일 (실제 색상으로 표시)
            '& strong': {
              fontFamily: "'NanumSquareB', 'NanumSquare', sans-serif",
              fontWeight: 'bold',
              color: '#111',
              letterSpacing: '-0.01em' // 볼드 텍스트도 일반 텍스트와 동일한 자간 유지
            },
            '& span[style*="font-size"]': {
              lineHeight: 'auto',
              color: '#111'
            }
          }}
          dangerouslySetInnerHTML={{
            __html: formattedText.formatting && formattedText.formatting.length > 0 
              ? renderFormattedText(formattedText)
              : (formattedText.text || '').replace(/\n/g, '<br>')
          }}
        />
        
        {/* TextArea (편집용) */}
        <textarea
          ref={editorRef}
          value={formattedText.text || ''}
          onChange={(e) => {
            const textarea = e.target;
            const newText = textarea.value;
            const oldText = formattedText.text || '';
            const cursorPosition = textarea.selectionStart;
            
            // 텍스트 변경량 계산
            const textDiff = newText.length - oldText.length;
            
            // 포맷팅 범위 조정: 텍스트가 삽입/삭제되면 포맷팅 범위도 조정
            let adjustedFormatting = formattedText.formatting || [];
            
            if (textDiff !== 0 && adjustedFormatting.length > 0) {
              // 실제 변경된 위치를 찾기 위해 텍스트 비교
              // 커서 위치를 기준으로 변경된 위치 추정
              const changePosition = cursorPosition - (textDiff > 0 ? textDiff : 0);
              
              // 포맷팅 범위 조정
              adjustedFormatting = adjustedFormatting.map(format => {
                // 변경 위치 이후의 포맷팅 범위만 조정
                if (format.start >= changePosition) {
                  // 변경 위치 이후의 포맷팅은 전체 범위를 이동
                  return {
                    ...format,
                    start: Math.max(0, format.start + textDiff),
                    end: Math.max(0, format.end + textDiff)
                  };
                } else if (format.end > changePosition) {
                  // 변경 위치가 포맷팅 범위 내에 있는 경우
                  // 포맷팅의 끝 부분만 조정
                  return {
                    ...format,
                    end: Math.max(format.start, format.end + textDiff)
                  };
                }
                // 변경 위치 이전의 포맷팅은 그대로 유지
                return format;
              }).filter(format => {
                // 유효한 포맷팅만 유지
                return format.start < format.end && 
                       format.start >= 0 && 
                       format.end <= newText.length &&
                       format.start < newText.length &&
                       format.end > 0;
              });
            }
            
            const newFormattedText = {
              text: newText,
              formatting: adjustedFormatting
            };
            
            setFormattedText(newFormattedText);
            onChange(newFormattedText);
            
            // TextArea는 자체적으로 커서 위치를 관리하므로 강제 조정하지 않음
            // 사용자 입력이 방해받지 않도록 함
          }}
          onSelect={handleTextSelection}
          onScroll={handleScroll}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className="formatted-text-editor-textarea"
          style={{
            width: '100%',
            height: '100%',
            padding: isMobile ? '20px 16px' : '24px 20px',
            fontSize: isMobile ? '16px' : '1.01rem',
            lineHeight: '1.7',
            fontFamily: 'NanumSquareR, sans-serif',
            fontWeight: 400,
            letterSpacing: '-0.01em',
            color: formattedText.formatting && formattedText.formatting.length > 0 
              ? 'transparent' // 포맷팅이 있으면 투명하게 하여 미리보기가 보이도록
              : '#111', // 포맷팅이 없으면 일반 텍스트 색상
            caretColor: '#111', // caret 항상 표시 (color:transparent 시에도 깜빡임 유지)
            background: 'transparent', // 배경은 투명 (부모 배경 사용)
            border: 'none',
            borderRadius: 0,
            outline: 'none',
            overflowY: 'auto',
            boxSizing: 'border-box',
            cursor: 'text',
            resize: 'none',
            position: 'relative',
            zIndex: 11, // 항상 overlay 위에 배치하여 caret/selection 항상 노출
            isolation: 'isolate', // 새로운 stacking context 생성하여 z-index 충돌 방지
          }}
          onFocus={(e) => {
            // 포커스 시 시각적 피드백 제거 (깔끔한 디자인)
          }}
          onBlur={(e) => {
            // 블러 시 시각적 피드백 제거
          }}
        />
      </Box>

      {/* 바텀시트 형태의 포맷팅 메뉴 */}
      {!disableFormatMenu && formatMenuAnchor && (
        <>
          {/* 오버레이 - 클릭 시 바텀시트 닫기 */}
          <Box
            onClick={handleCloseFormatMenu}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1299,
              pointerEvents: 'auto',
              transition: 'background-color 0.2s ease'
            }}
          />
          
          {/* 바텀시트 */}
          <Box
            sx={{
              position: 'fixed',
              bottom: isMobile && keyboardHeight > 0 ? `${keyboardHeight}px` : 0,
              left: 0,
              right: 0,
              width: '100%',
              bgcolor: '#ffffff',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              boxShadow: '0px -4px 16px rgba(0, 0, 0, 0.15)',
              zIndex: 1300,
              transform: formatMenuAnchor ? 'translateY(0)' : 'translateY(100%)',
              transition: isMobile && keyboardHeight > 0 
                ? 'bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              fontFamily: 'NanumSquareR, sans-serif',
              // 모바일에서 키보드 위에 표시되도록 추가 스타일
              ...(isMobile && keyboardHeight > 0 && {
                maxHeight: `calc(100vh - ${keyboardHeight}px)`,
                overflowY: 'auto'
              })
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 바텀시트 핸들 (최소화) */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              pt: 1,
              pb: 0.5
            }}>
              <Box sx={{
                width: 32,
                height: 3,
                bgcolor: '#d0d0d0',
                borderRadius: '2px'
              }} />
            </Box>

            {/* 포맷팅 버튼들 (컴팩트하게) */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              justifyContent: 'center',
              px: 2,
              pb: 2,
              pt: 0.5
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25
              }}>
                <IconButton
                  size="medium"
                  onClick={handleBold}
                  disabled={isComposing}
                  sx={{
                    bgcolor: '#f8f9fa',
                    width: 48,
                    height: 48,
                    border: '1px solid #e9ecef',
                    '&:hover': { 
                      bgcolor: '#e9ecef',
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    },
                    '&:active': {
                      transform: 'scale(0.95)'
                    },
                    '&:disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                      transform: 'none'
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <FormatBold fontSize="medium" sx={{ color: '#495057' }} />
                </IconButton>
                <Typography variant="caption" sx={{
                  fontFamily: 'NanumSquareR, sans-serif',
                  fontSize: '0.65rem',
                  color: '#666',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}>
                  볼드
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25
              }}>
                <IconButton
                  size="medium"
                  onClick={handleTitle}
                  disabled={isComposing}
                  sx={{
                    bgcolor: '#f8f9fa',
                    width: 48,
                    height: 48,
                    border: '1px solid #e9ecef',
                    '&:hover': { 
                      bgcolor: '#e9ecef',
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    },
                    '&:active': {
                      transform: 'scale(0.95)'
                    },
                    '&:disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                      transform: 'none'
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Title fontSize="medium" sx={{ color: '#495057' }} />
                </IconButton>
                <Typography variant="caption" sx={{
                  fontFamily: 'NanumSquareR, sans-serif',
                  fontSize: '0.65rem',
                  color: '#666',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}>
                  소제목
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25
              }}>
                <IconButton
                  size="medium"
                  onClick={handleClearFormatting}
                  disabled={isComposing}
                  sx={{
                    bgcolor: '#f8f9fa',
                    width: 48,
                    height: 48,
                    border: '1px solid #e9ecef',
                    '&:hover': { 
                      bgcolor: '#e9ecef',
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    },
                    '&:active': {
                      transform: 'scale(0.95)'
                    },
                    '&:disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                      transform: 'none'
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <FormatClear fontSize="medium" sx={{ color: '#495057' }} />
                </IconButton>
                <Typography variant="caption" sx={{
                  fontFamily: 'NanumSquareR, sans-serif',
                  fontSize: '0.65rem',
                  color: '#666',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}>
                  제거
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
});

FormattedTextEditor.displayName = 'FormattedTextEditor';

export default FormattedTextEditor;